import type { Component, Ref } from 'vue';
import { computed } from 'vue';
import { useBetConstructStream } from '@/composable/liveStream/useBetConstructStream';
import { useBetGeniusStream } from '@/composable/liveStream/useBetGeniusStream';
import { useBetRadarStream } from '@/composable/liveStream/useBetRadarStream';
import { useGLiveStream } from '@/composable/liveStream/useGLiveStream';
import { useMuayThaiStream } from '@/composable/liveStream/useMuayThaiStream';
import { useTwitchStream } from '@/composable/liveStream/useTwitchStream';
import { openLiveStream } from '@/core/lib/liveStream';
import type { IStreamEvent } from '@/interface/IStream';
import { SportType } from '@/interface/enum';
import type { LiveStreamProvider } from '@/interface/enum';

export type UseLiveStreamEvent = Pick<IStreamEvent, 'stream' | 'isLive' | 'isSimulation' | 'sportType' | 'league'>;

export type UseLiveStreamProvider = {
    isAvailable: Ref<boolean>;
    isPlayable: Ref<boolean>;
    streamDisplay: Ref<{
        provider: LiveStreamProvider;
        component: Component;
        key: string;
        props: Record<string, any>;
    } | null>;
};

export function useLiveStream(event: Ref<UseLiveStreamEvent | null>) {
    const streamProviders: UseLiveStreamProvider[] = [
        useTwitchStream(event),
        useMuayThaiStream(event),
        useBetRadarStream(event),
        useGLiveStream(event),
        useBetConstructStream(event),
        useBetGeniusStream(event),
    ];

    const isLiveStreamAvailable = computed(() => {
        if (!event.value?.stream) return false;
        return streamProviders.some(provider => provider.isAvailable.value);
    });

    const isLiveStreamPlayable = computed(() => {
        if (!event.value?.stream) return false;
        if (!event.value?.isLive) {
            // Before kick-off only real soccer may show the stream; e-football (simulation) must not.
            if (event.value?.sportType !== SportType.Soccer) return false;
            if (event.value?.isSimulation) return false;
        }
        return streamProviders.some(provider => provider.isPlayable.value);
    });

    const streamDisplay = computed(() => {
        if (!event.value?.stream) return null;
        if (!isLiveStreamPlayable.value) return null;
        return streamProviders.find(provider => provider.isPlayable.value)?.streamDisplay.value;
    });

    return {
        isLiveStreamAvailable,
        isLiveStreamPlayable,
        streamDisplay,
        openLiveStream,
    };
}
